import "@milkdown/crepe/theme/common/style.css";
import "@milkdown/crepe/theme/frame-dark.css";
import "./milkdown-markdown.css";

import { Crepe } from "@milkdown/crepe";
import { MarkdownSurface } from "@thilke/ui/components/markdown-surface";
import { cn } from "@thilke/ui/lib/utils";
import { useEffect, useRef } from "react";

export interface MilkdownMarkdownApi {
  getMarkdown: () => string;
}

export interface MilkdownMarkdownProps {
  value: string;
  editing?: boolean;
  variant?: "display" | "editor";
  density?: "compact" | "document";
  className?: string;
  ariaLabel?: string;
  onReady?: (api: MilkdownMarkdownApi) => void;
}

function featuresForVariant(variant: "display" | "editor") {
  if (variant === "editor") {
    return {
      [Crepe.Feature.AI]: false,
    };
  }

  return {
    [Crepe.Feature.AI]: false,
    [Crepe.Feature.BlockEdit]: false,
    [Crepe.Feature.Cursor]: false,
    [Crepe.Feature.LinkTooltip]: false,
    [Crepe.Feature.Placeholder]: false,
    [Crepe.Feature.Toolbar]: false,
    [Crepe.Feature.TopBar]: false,
  };
}

export function MilkdownMarkdown({
  value,
  editing = false,
  variant = "display",
  density = variant === "editor" ? "document" : "compact",
  className,
  ariaLabel = "Markdown content",
  onReady,
}: MilkdownMarkdownProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Crepe | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const editor = new Crepe({
      root,
      defaultValue: value,
      features: featuresForVariant(variant),
    });
    editorRef.current = editor;
    editor.create().then(() => {
      editor.setReadonly(!editing);
      onReady?.({ getMarkdown: () => editor.getMarkdown() });
    });

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, [editing, onReady, value, variant]);

  useEffect(() => {
    editorRef.current?.setReadonly(!editing);
  }, [editing]);

  return (
    <MarkdownSurface
      density={density}
      className={cn("milkdown-markdown", className)}
      data-density={density}
      data-variant={variant}
      role="article"
      aria-label={ariaLabel}
    >
      <div ref={rootRef} />
    </MarkdownSurface>
  );
}
